import { Component, OnInit, HostListener, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { ClientService } from '../../services/client.service';
import { BillingEntityService, BillingEntity } from '../../services/billing-entity.service';
import { InvoiceService } from '../../services/invoice.service';
import Swal from 'sweetalert2';

interface BillableService {
    id: number;
    folio: number | string | null;
    origin_address: string | null;
    destination_address: string | null;
    upload_date: string | null;
}

interface ConceptItem {
    service_id: number;
    folio: number | string | null;
    route: string;
    service_description: string;
    quantity: number;
    unit_key: string;
    unit_price: number;
    product_service_key: string;
    tax_object_key: string;
}

@Component({
    selector: 'app-invoicing-new',
    templateUrl: './invoicing-new.component.html',
    styleUrls: ['../../order/bill-service/bill-service.component.css']
})
export class InvoicingNewComponent implements OnInit {
    // --- Entidad emisora (paso 0) ---
    billingEntities: BillingEntity[] = [];
    selectedBillingEntityId: number | null = null;
    billingEntityOpen = false;

    // --- Cliente receptor (paso 1) ---
    clients: any[] = [];
    selectedClientId: number | null = null;
    clientOpen = false;
    clientSearchText = '';

    // Datos fiscales del receptor (autollenados desde el cliente, solo lectura).
    rfc = '';
    razonSocial = '';
    codigoPostal = '';

    // --- Configuración CFDI (paso 2) ---
    paymentForms: { key: string; name: string }[] = [
        { key: '01', name: 'Efectivo' },
        { key: '02', name: 'Cheque' },
        { key: '03', name: 'Transferencia electrónica' },
        { key: '04', name: 'Tarjeta de crédito' },
        { key: '28', name: 'Tarjeta de débito' },
        { key: '99', name: 'Por definir' }
    ];
    selectedPaymentFormKey: string | null = null;
    paymentFormOpen = false;

    paymentMethods: { key: string; name: string }[] = [
        { key: 'PPD', name: 'Pago en parcialidades o diferido' },
        { key: 'PUE', name: 'Pago en una sola exhibición' }
    ];
    selectedPaymentMethodKey: string | null = null;
    paymentMethodOpen = false;

    // Catálogos para editar cada concepto.
    units: { key: string; name: string }[] = [
        { key: 'E48', name: 'Unidad de Servicio' },
        { key: 'HUR', name: 'Hora' },
        { key: 'DAY', name: 'Día' },
        { key: 'MON', name: 'Mes' },
        { key: 'ACT', name: 'Actividad' },
        { key: 'C62', name: 'Uno' }
    ];
    productServiceKeys: { key: string; name: string }[] = [
        { key: '78101800', name: 'Servicios de transporte' },
        { key: '78101500', name: 'Servicios de transporte de carga' },
        { key: '78101600', name: 'Servicios de transporte especializado' },
        { key: '78102000', name: 'Servicios de almacenamiento' },
        { key: '78102100', name: 'Servicios de gestión de inventarios' },
        { key: '78102200', name: 'Servicios de distribución' },
        { key: '78102300', name: 'Servicios logísticos integrales' },
        { key: '01010101', name: 'No existe en el catálogo' }
    ];
    taxObjectOptions: { key: string; name: string }[] = [
        { key: '01', name: 'No objeto de impuesto' },
        { key: '02', name: 'Sí objeto de impuesto' }
    ];

    // --- Servicios a facturar (paso 3) ---
    billableServices: BillableService[] = [];
    loadingServices = false;
    servicesOpen = false;
    conceptItems: ConceptItem[] = [];

    isGenerating = false;

    constructor(
        private location: Location,
        private router: Router,
        private clientService: ClientService,
        private billingEntityService: BillingEntityService,
        private invoiceService: InvoiceService,
        private elementRef: ElementRef
    ) {}

    ngOnInit(): void {
        this.billingEntityService.list().subscribe({
            next: (res) => { this.billingEntities = Array.isArray(res) ? res : []; },
            error: () => { this.billingEntities = []; }
        });
        this.clientService.listClient().subscribe({
            next: (res: any) => { this.clients = Array.isArray(res) ? res : []; },
            error: () => { this.clients = []; }
        });
    }

    private closeAll(except?: string): void {
        const keys = ['billingEntityOpen', 'clientOpen', 'paymentFormOpen', 'paymentMethodOpen', 'servicesOpen'];
        keys.forEach(k => { if (k !== except) (this as any)[k] = false; });
    }

    // ---------- Entidad emisora ----------
    get selectedBillingEntity(): BillingEntity | null {
        return this.billingEntities.find(e => e.id === this.selectedBillingEntityId) ?? null;
    }
    toggleBillingEntity(): void {
        const next = !this.billingEntityOpen;
        this.closeAll('billingEntityOpen');
        this.billingEntityOpen = next;
    }
    selectBillingEntity(id: number): void {
        this.selectedBillingEntityId = id;
        this.billingEntityOpen = false;
    }

    onAddBillingEntity(): void {
        this.billingEntityOpen = false;
        Swal.fire({
            title: 'Nueva entidad de factura',
            width: 620,
            html:
                '<input id="swal-entity-name" class="swal2-input" placeholder="Nombre comercial">' +
                '<input id="swal-entity-legal" class="swal2-input" placeholder="Razón social (exacta como SAT)">' +
                '<input id="swal-entity-rfc" class="swal2-input" placeholder="RFC (12-13 caracteres)" maxlength="13">' +
                '<input id="swal-entity-regimen" class="swal2-input" placeholder="Régimen fiscal (ej. 601)" maxlength="3">' +
                '<input id="swal-entity-cp" class="swal2-input" placeholder="Código postal" maxlength="5">' +
                '<input id="swal-entity-serie" class="swal2-input" placeholder="Serie (opcional, ej. F)" maxlength="10">' +
                '<hr style="margin: 12px 0;"><p style="margin: 0 0 8px; font-size: 13px; color: #6c757d;">Domicilio fiscal</p>' +
                '<input id="swal-entity-street" class="swal2-input" placeholder="Calle">' +
                '<input id="swal-entity-ext" class="swal2-input" placeholder="Número exterior">' +
                '<input id="swal-entity-cologne" class="swal2-input" placeholder="Colonia">' +
                '<input id="swal-entity-locality" class="swal2-input" placeholder="Localidad/Ciudad">' +
                '<input id="swal-entity-municipality" class="swal2-input" placeholder="Municipio/Alcaldía">' +
                '<input id="swal-entity-state" class="swal2-input" placeholder="Estado">' +
                '<input id="swal-entity-country" class="swal2-input" placeholder="País" value="México">' +
                '<hr style="margin: 12px 0;"><p style="margin: 0 0 8px; font-size: 13px; color: #6c757d;">Certificado de Sello Digital (simulado, sin PAC real)</p>' +
                '<input id="swal-entity-issuer-csd" class="swal2-input" placeholder="No. de serie del CSD del emisor">',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Agregar',
            cancelButtonText: 'Cancelar',
            preConfirm: () => {
                const name = (document.getElementById('swal-entity-name') as HTMLInputElement | null)?.value?.trim();
                const legal = (document.getElementById('swal-entity-legal') as HTMLInputElement | null)?.value?.trim();
                const rfc = (document.getElementById('swal-entity-rfc') as HTMLInputElement | null)?.value?.trim();
                const regimen = (document.getElementById('swal-entity-regimen') as HTMLInputElement | null)?.value?.trim();
                const cp = (document.getElementById('swal-entity-cp') as HTMLInputElement | null)?.value?.trim();
                const serie = (document.getElementById('swal-entity-serie') as HTMLInputElement | null)?.value?.trim();
                const street = (document.getElementById('swal-entity-street') as HTMLInputElement | null)?.value?.trim();
                const exteriorNumber = (document.getElementById('swal-entity-ext') as HTMLInputElement | null)?.value?.trim();
                const cologne = (document.getElementById('swal-entity-cologne') as HTMLInputElement | null)?.value?.trim();
                const locality = (document.getElementById('swal-entity-locality') as HTMLInputElement | null)?.value?.trim();
                const municipality = (document.getElementById('swal-entity-municipality') as HTMLInputElement | null)?.value?.trim();
                const state = (document.getElementById('swal-entity-state') as HTMLInputElement | null)?.value?.trim();
                const country = (document.getElementById('swal-entity-country') as HTMLInputElement | null)?.value?.trim();
                const issuerCsdSerial = (document.getElementById('swal-entity-issuer-csd') as HTMLInputElement | null)?.value?.trim();
                if (!name || !legal || !rfc || !regimen || !cp) {
                    Swal.showValidationMessage('Todos los campos excepto serie, domicilio y CSD son obligatorios');
                    return false;
                }
                return {
                    name, legal_name: legal, rfc, regimen_fiscal_key: regimen, postal_code: cp, serie: serie || null,
                    street: street || null, exterior_number: exteriorNumber || null, cologne: cologne || null,
                    locality: locality || null, municipality: municipality || null, state: state || null, country: country || null,
                    issuer_csd_serial: issuerCsdSerial || null,
                };
            }
        }).then(result => {
            if (!result.isConfirmed || !result.value) return;
            this.billingEntityService.create(result.value as Partial<BillingEntity>).subscribe({
                next: (entity) => {
                    this.billingEntities = [...this.billingEntities, entity];
                    this.selectedBillingEntityId = entity.id;
                },
                error: (err) => {
                    Swal.fire('Error', err?.error?.error ?? 'No se pudo guardar la entidad', 'error');
                }
            });
        });
    }

    // ---------- Cliente receptor ----------
    get selectedClient(): any | null {
        return this.clients.find(c => c.id === this.selectedClientId) ?? null;
    }
    get filteredClients(): any[] {
        const term = (this.clientSearchText || '').trim().toLowerCase();
        if (!term) return this.clients;
        return this.clients.filter(c =>
            [c?.business_name, c?.name, c?.rfc].map(v => String(v ?? '').toLowerCase()).join(' ').includes(term)
        );
    }
    toggleClient(): void {
        const next = !this.clientOpen;
        this.closeAll('clientOpen');
        this.clientOpen = next;
        if (!this.clientOpen) this.clientSearchText = '';
    }
    selectClient(client: any): void {
        this.selectedClientId = client.id;
        this.clientOpen = false;
        this.clientSearchText = '';
        // Autollenar datos fiscales del receptor (informativos; el backend los
        // resuelve del cliente del servicio).
        this.rfc = client?.rfc ?? '';
        this.razonSocial = client?.business_name ?? client?.name ?? '';
        this.codigoPostal = client?.postal_code ?? '';
        // Al cambiar de cliente se reinician los servicios/conceptos.
        this.conceptItems = [];
        this.billableServices = [];
        this.loadBillableServices(client.id);
    }
    private loadBillableServices(clientId: number): void {
        this.loadingServices = true;
        this.invoiceService.billableServices(clientId).subscribe({
            next: (res) => { this.billableServices = Array.isArray(res) ? res : []; this.loadingServices = false; },
            error: () => { this.billableServices = []; this.loadingServices = false; }
        });
    }

    // ---------- Forma / método de pago ----------
    get selectedPaymentForm(): { key: string; name: string } | null {
        return this.paymentForms.find(f => f.key === this.selectedPaymentFormKey) ?? null;
    }
    togglePaymentForm(): void {
        const next = !this.paymentFormOpen;
        this.closeAll('paymentFormOpen');
        this.paymentFormOpen = next;
    }
    selectPaymentForm(key: string): void {
        this.selectedPaymentFormKey = key;
        this.paymentFormOpen = false;
    }
    get selectedPaymentMethod(): { key: string; name: string } | null {
        return this.paymentMethods.find(m => m.key === this.selectedPaymentMethodKey) ?? null;
    }
    togglePaymentMethod(): void {
        const next = !this.paymentMethodOpen;
        this.closeAll('paymentMethodOpen');
        this.paymentMethodOpen = next;
    }
    selectPaymentMethod(key: string): void {
        this.selectedPaymentMethodKey = key;
        this.paymentMethodOpen = false;
        // SAT CFDI40105: con PPD la forma de pago debe ser 99 (Por definir).
        if (key === 'PPD') {
            this.selectedPaymentFormKey = '99';
        }
    }
    get isPaymentFormLocked(): boolean {
        return this.selectedPaymentMethodKey === 'PPD';
    }

    // ---------- Servicios a facturar / conceptos ----------
    toggleServices(): void {
        if (!this.selectedClientId) return;
        const next = !this.servicesOpen;
        this.closeAll('servicesOpen');
        this.servicesOpen = next;
    }
    isServiceSelected(serviceId: number): boolean {
        return this.conceptItems.some(i => i.service_id === serviceId);
    }
    toggleServiceSelection(svc: BillableService): void {
        if (this.isServiceSelected(svc.id)) {
            this.conceptItems = this.conceptItems.filter(i => i.service_id !== svc.id);
            return;
        }
        const route = [svc.origin_address, svc.destination_address]
            .filter(Boolean).join(' → ') || '';
        this.conceptItems.push({
            service_id: svc.id,
            folio: svc.folio,
            route,
            service_description: 'Servicio de transporte de carga',
            quantity: 1,
            unit_key: 'E48',
            unit_price: 0,
            product_service_key: '78101800',
            tax_object_key: '02',
        });
    }
    removeConcept(serviceId: number): void {
        this.conceptItems = this.conceptItems.filter(i => i.service_id !== serviceId);
    }
    conceptSubtotal(item: ConceptItem): number {
        return (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
    }

    get subtotal(): number {
        return this.conceptItems.reduce((s, i) => s + this.conceptSubtotal(i), 0);
    }
    get iva(): number {
        return this.conceptItems.reduce(
            (s, i) => s + (i.tax_object_key === '02' ? this.conceptSubtotal(i) * 0.16 : 0), 0);
    }
    get total(): number {
        return this.subtotal + this.iva;
    }

    private isConceptValid(i: ConceptItem): boolean {
        return !!(
            i.service_description?.trim() &&
            i.quantity != null && i.quantity > 0 &&
            i.unit_key &&
            i.unit_price != null && i.unit_price > 0 &&
            i.product_service_key &&
            i.tax_object_key
        );
    }
    get isFormValid(): boolean {
        return !!(
            this.selectedBillingEntityId &&
            this.selectedClientId &&
            this.selectedPaymentFormKey &&
            this.selectedPaymentMethodKey &&
            this.conceptItems.length > 0 &&
            this.conceptItems.every(i => this.isConceptValid(i))
        );
    }

    onCancel(): void {
        this.goBack();
    }

    onGenerateInvoice(): void {
        if (!this.isFormValid || this.isGenerating) return;
        this.isGenerating = true;
        this.invoiceService.issueConsolidated({
            billing_entity_id: Number(this.selectedBillingEntityId),
            payment_form_key: this.selectedPaymentFormKey as string,
            payment_method_key: this.selectedPaymentMethodKey as string,
            items: this.conceptItems.map(i => ({
                service_id: i.service_id,
                service_description: i.service_description.trim(),
                quantity: Number(i.quantity),
                unit_key: i.unit_key,
                unit_price: Number(i.unit_price),
                product_service_key: i.product_service_key,
                tax_object_key: i.tax_object_key,
            })),
        }).subscribe({
            next: (res) => {
                this.isGenerating = false;
                Swal.fire({
                    icon: 'success',
                    title: 'CFDI timbrado',
                    html: `UUID: <b>${res.uuid}</b><br>Folio: <b>${res.invoice?.folio ?? ''}</b><br>Servicios: <b>${(res.service_ids ?? []).join(', ')}</b>`,
                    confirmButtonText: 'Ver factura'
                }).then(() => {
                    const folio = res.invoice?.folio;
                    if (folio) {
                        this.router.navigate(['/service/list/folio-' + folio + '/view']);
                    } else {
                        this.router.navigate(['/invoicing/list']);
                    }
                });
            },
            error: (err) => {
                this.isGenerating = false;
                const ve = err?.error?.validation_errors;
                if (Array.isArray(ve) && ve.length) {
                    const items = ve.map((e: any) => `<li><b>${e.code}</b> — ${e.message}</li>`).join('');
                    Swal.fire({
                        title: 'Datos inválidos (no se gastó timbre)',
                        html: `<ul style="text-align:left;margin:0;padding-left:18px">${items}</ul>`,
                        icon: 'warning',
                    });
                    return;
                }
                const msg = err?.error?.error ?? 'No se pudo generar la factura.';
                Swal.fire('Error al timbrar', msg, 'error');
            }
        });
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.closeAll();
        }
    }

    goBack(): void {
        if (window.history.length > 1) {
            this.location.back();
        } else {
            this.router.navigate(['/invoicing/list']);
        }
    }
}
